import { useEffect, useState } from 'react'
import { InlineAlert } from '../../../components/InlineAlert'
import { listCalculationGraphs } from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { CalculationGraphView } from '../../../types/cartridgeStudio'
import './RuleSetPicker.css'

export interface CalcGraphPickerProps {
  programId: string
  selectedId: string | null
  onSelect: (graph: CalculationGraphView) => void
  refreshToken?: number
}

/** `GET /programs/{programId}/calculation-graphs` — a compact per-program picker, same rationale as `RuleSetPicker`. */
export function CalcGraphPicker({ programId, selectedId, onSelect, refreshToken }: CalcGraphPickerProps) {
  const [graphs, setGraphs] = useState<CalculationGraphView[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // See ProgramWorkspaceScreen's effect for why the `controller.signal.aborted` guard is needed
    // (React 18 StrictMode double-invoke race).
    const controller = new AbortController()
    setError(null)
    listCalculationGraphs(programId, controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return
        setGraphs(list)
        if (!selectedId && list.length > 0) onSelect(list[list.length - 1]!)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
        else setError('Could not load CalculationGraphs for this program.')
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, refreshToken])

  if (error) return <InlineAlert severity="error">{error}</InlineAlert>
  if (graphs === null) return <div style={{ color: 'var(--iw-text-muted)' }}>Loading…</div>
  if (graphs.length === 0) return <div style={{ color: 'var(--iw-text-muted)' }}>No CalculationGraphs yet for this program.</div>

  return (
    <div className="iw-rspicker" style={{ minWidth: 220 }}>
      <div className="iw-rspicker__header">
        <span className="iw-rspicker__title">Graph versions</span>
      </div>
      <ul className="iw-rspicker__list" role="list">
        {graphs.map((g) => (
          <li key={g.id}>
            <button
              type="button"
              className={g.id === selectedId ? 'iw-rspicker__row iw-rspicker__row--selected' : 'iw-rspicker__row'}
              onClick={() => onSelect(g)}
            >
              <span className="iw-rspicker__version">v{g.graphVersion}</span>
              <span className="iw-rspicker__dates">
                {g.validFrom} → {g.validTo ?? 'open'}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
